/**
 * eyeAim — pure gaze math for the Eye Follow node (ENG-1060).
 *
 * Two jobs, both pure and dependency-free so they are unit-testable without
 * THREE or the render loop (same reason `headAim.js` exists):
 *
 *   1. Resolve a direction-to-target into a clamped yaw/pitch pair, using the
 *      SAME body-relative yaw clamp the neck aim uses. Eye and neck must agree
 *      on what "yaw" means — the ticket's 70/30 split is a split of ONE angle,
 *      and two independent conventions is exactly how they would silently
 *      disagree once the avatar walks and `movementComponent` yaws the group.
 *   2. Spend that yaw/pitch as ARKit `EyeLook*` channel weights. That is the one
 *      currency every renderer here accepts: GLB morphs via
 *      `ARKIT_TO_METAHUMAN`, and the splat rig via `arkitToMh`. A
 *      gaze-conditioned eye branch takes the ANGLES instead — it has real
 *      eyeballs and its own cone clamp.
 *
 * Sign conventions, and why they are written down rather than felt out:
 * yaw comes from `atan2(x, z)` with +Z as rest-forward (the convention
 * `movementComponent` and `headFollowRuntime` already share — see the note at
 * `EventRuntime.js`'s rest-forward comment). Under glTF's right-handed, +Y-up,
 * +Z-forward frame, +X is the character's LEFT. So a POSITIVE yaw means the
 * target is to the character's left, which is `EyeLookOut` on the left eye and
 * `EyeLookIn` on the right — "in" being toward the nose, per the swap comments
 * on `ARKIT_TO_METAHUMAN`'s EyeLookIn/Out rows. Positive pitch (`asin(y)`) is a
 * target ABOVE the eyes, i.e. `EyeLookUp`.
 *
 * Getting either sign backwards renders as a cross-eyed avatar or one looking
 * away from the camera, so both are covered by tests — but a test only pins the
 * convention this file DECLARES. That the declared convention matches the actual
 * rig is settled by looking at the avatar, not by this module.
 */

import { clampYawToBody } from './headAim.js'

/** ARKit channel indices (see `arkitNames.js` — the server's own order). */
export const EYE_LOOK_DOWN_LEFT = 1
export const EYE_LOOK_IN_LEFT = 2
export const EYE_LOOK_OUT_LEFT = 3
export const EYE_LOOK_UP_LEFT = 4
export const EYE_LOOK_DOWN_RIGHT = 8
export const EYE_LOOK_IN_RIGHT = 9
export const EYE_LOOK_OUT_RIGHT = 10
export const EYE_LOOK_UP_RIGHT = 11

/** Every channel this module ever writes — the caller's zero-out list. */
export const EYE_LOOK_CHANNELS = [
  EYE_LOOK_DOWN_LEFT, EYE_LOOK_IN_LEFT, EYE_LOOK_OUT_LEFT, EYE_LOOK_UP_LEFT,
  EYE_LOOK_DOWN_RIGHT, EYE_LOOK_IN_RIGHT, EYE_LOOK_OUT_RIGHT, EYE_LOOK_UP_RIGHT,
]

/**
 * Direction-to-target → clamped yaw/pitch, relative to the REFERENCE frame the
 * eyes sit in.
 *
 * That reference is the HEAD's current forward, not the body's, and the
 * distinction is the whole ballgame when head-follow is also running. The neck
 * turns toward the target first and consumes most of the angle; the eyes are
 * carried along by it and only need the REMAINDER. Measured on myra with the
 * camera 45.2° off body-forward and head-follow at full weight: the head had
 * already turned 43.1°, leaving 2.2° for the eyes — while a body-relative aim
 * drove them 34°, so they shot straight past the camera. The better head-follow
 * worked, the more wrong the eyes looked.
 *
 * With no head-follow in the graph the head's forward IS the body's, so this
 * reduces to the previous behaviour exactly.
 *
 * @param {number} dx  target direction x (need not be normalized)
 * @param {number} dy  target direction y
 * @param {number} dz  target direction z
 * @param {{yaw:number,pitch:number}} ref  the frame the eyes rest in (rad, world)
 * @param {number} maxYaw   max eye yaw from that frame (rad, ≥ 0)
 * @param {number} maxPitch max eye pitch from that frame (rad, ≥ 0)
 * @param {{yaw:number,pitch:number}} out  caller-owned result (no allocation)
 * @returns {{yaw:number,pitch:number}} `out`, yaw/pitch in radians, ref-relative
 */
export function aimAngles(dx, dy, dz, ref, maxYaw, maxPitch, out) {
  const len = Math.hypot(dx, dy, dz)
  if (len === 0) {
    // No direction at all (target sits exactly on the pivot). Rest, rather than
    // an arbitrary angle from a degenerate atan2.
    out.yaw = 0
    out.pitch = 0
    return out
  }
  const rawYaw = Math.atan2(dx, dz)
  // asin's domain: dy/len is already in [-1, 1] by construction, but clamp so
  // float error at exactly ±len can't produce NaN and poison the rig.
  const sinPitch = Math.max(-1, Math.min(1, dy / len))
  const rawPitch = Math.asin(sinPitch)

  // Both axes are measured FROM the reference frame — clampYawToBody does the
  // wrap-and-clamp for yaw; pitch is a plain subtraction (no wrap: a head can't
  // pitch past ±90° without the yaw already having flipped).
  out.yaw = clampYawToBody(rawYaw, ref.yaw ?? 0, maxYaw)
  const relPitch = rawPitch - (ref.pitch ?? 0)
  out.pitch = Math.max(-maxPitch, Math.min(maxPitch, relPitch))
  return out
}

/**
 * Yaw/pitch → the 8 ARKit EyeLook channel weights.
 *
 * Normalized against the CALIBRATION range, not against 90°: an ARKit
 * `EyeLookOut` of 1.0 means the eye is at its own mechanical limit, so mapping
 * the authored `yawRange` onto 1.0 is what makes that authored number mean what
 * it says. A node calibrated at yawRange 35° that only ever emitted 0.39 (35/90)
 * would read as "the feature barely works".
 *
 * `yawRange`/`pitchRange` are the CALIBRATION, deliberately DISTINCT from the aim
 * CLAMP (maxYaw/maxPitch, applied in `aimAngles` before this runs): the clamp
 * bounds how far the eye may point, the range sets which aim angle counts as
 * fully deflected. Passing the clamp here — as the runtime did before the two
 * were split — is exactly the trap where raising the "limit" to loosen it instead
 * shrank every morph, because a bigger denominator is less deflection per degree.
 *
 * Writes into `out` (an array/typed-array/object indexed by ARKit channel) and
 * ZEROES the opposing channels, so a caller holding one scratch object across
 * frames never carries a stale left-look into a right-look. Only the 8 EyeLook
 * channels are touched — blink, squint and everything else are somebody else's.
 *
 * @param {number} yaw    body-relative yaw (rad); + = target to character's left
 * @param {number} pitch  pitch (rad); + = target above
 * @param {number} yawRange   aim yaw that maps to a fully-deflected eye (rad, > 0)
 * @param {number} pitchRange aim pitch that maps to a fully-deflected eye (rad, > 0)
 * @param {number} weight scale applied to every channel (the node's eye weight × gate)
 * @param {object|Array} out caller-owned, indexed by ARKit channel
 * @returns {object|Array} `out`
 */
export function arkitEyeChannels(yaw, pitch, yawRange, pitchRange, weight, out) {
  const w = weight <= 0 ? 0 : weight > 1 ? 1 : weight
  const yn = yawRange > 0 ? clamp01(Math.abs(yaw) / yawRange) * w : 0
  const pn = pitchRange > 0 ? clamp01(Math.abs(pitch) / pitchRange) * w : 0

  const leftward = yaw > 0 // target to the character's left
  // Left eye: leftward is AWAY from the nose (out). Right eye: leftward is
  // TOWARD the nose (in).
  out[EYE_LOOK_OUT_LEFT] = leftward ? yn : 0
  out[EYE_LOOK_IN_RIGHT] = leftward ? yn : 0
  out[EYE_LOOK_IN_LEFT] = leftward ? 0 : yn
  out[EYE_LOOK_OUT_RIGHT] = leftward ? 0 : yn

  const up = pitch > 0
  out[EYE_LOOK_UP_LEFT] = up ? pn : 0
  out[EYE_LOOK_UP_RIGHT] = up ? pn : 0
  out[EYE_LOOK_DOWN_LEFT] = up ? 0 : pn
  out[EYE_LOOK_DOWN_RIGHT] = up ? 0 : pn

  return out
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v
}
